import type { CSSProperties, SVGProps } from 'react';

export type IconName =
  | 'safe'
  | 'key'
  | 'lock'
  | 'fingerprint'
  | 'heart'
  | 'heartFilled'
  | 'people'
  | 'sparkle'
  | 'settings'
  | 'login'
  | 'card'
  | 'note'
  | 'identity'
  | 'search'
  | 'filter'
  | 'plus'
  | 'x'
  | 'launch'
  | 'edit'
  | 'copy'
  | 'eye'
  | 'eyeOff'
  | 'chevronRight'
  | 'chevronLeft'
  | 'chevronDown'
  | 'download'
  | 'upload'
  | 'signOut'
  | 'check'
  | 'view'
  | 'contact'
  | 'phone'
  | 'mail'
  | 'folder'
  | 'calendar'
  | 'checklist';

const ICON_PATHS: Record<IconName, JSX.Element> = {
  safe: (
    <g>
      <rect x="3.5" y="6" width="17" height="14" rx="3" />
      <circle cx="11" cy="13" r="3.25" />
      <path d="M11 13l-1.5-1.5" />
      <path d="M17 10.5v5" />
    </g>
  ),
  key: (
    <g>
      <circle cx="8" cy="15.5" r="3.75" />
      <path d="M10.65 12.85 20 3.5" />
      <path d="M16.5 7l2.5 2.5" />
    </g>
  ),
  lock: (
    <g>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.75" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </g>
  ),
  fingerprint: (
    <g>
      <path d="M5.5 11a6.5 6.5 0 0 1 13 0v2" />
      <path d="M8.5 11.5a3.5 3.5 0 0 1 7 0v3" />
      <path d="M11.5 12v4a2 2 0 0 0 2 2" />
      <path d="M6.5 16.5c.5 1 1 1.75 1.5 2.5" />
      <path d="M2.5 13.5a9.5 9.5 0 0 1 1.5-7" />
      <path d="M21.5 15.5a9.5 9.5 0 0 0 0-6" />
    </g>
  ),
  heart: (
    <path d="M12 20.5s-7.5-4.5-9.25-9.25C1 7 4.25 3.5 7.75 4.25 9.5 4.6 11 6 12 7.5c1-1.5 2.5-2.9 4.25-3.25 3.5-.75 6.75 2.75 5 7C19.5 16 12 20.5 12 20.5z" />
  ),
  heartFilled: (
    <path
      d="M12 20.5s-7.5-4.5-9.25-9.25C1 7 4.25 3.5 7.75 4.25 9.5 4.6 11 6 12 7.5c1-1.5 2.5-2.9 4.25-3.25 3.5-.75 6.75 2.75 5 7C19.5 16 12 20.5 12 20.5z"
      fill="currentColor"
    />
  ),
  people: (
    <g>
      <circle cx="9" cy="9" r="3.5" />
      <path d="M2.5 20c.5-3 2.75-5 6.5-5s6 2 6.5 5" />
      <circle cx="17" cy="7.5" r="2.75" />
      <path d="M21.5 17.5c-.5-2.25-2-3.75-4.5-3.75" />
    </g>
  ),
  sparkle: (
    <g>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="m6.5 6.5 2.5 2.5M15 15l2.5 2.5M6.5 17.5 9 15M15 9l2.5-2.5" />
    </g>
  ),
  settings: (
    <g>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.5 14.25a1.5 1.5 0 0 0 .3 1.65l.05.05a1.85 1.85 0 1 1-2.6 2.6l-.05-.05a1.5 1.5 0 0 0-1.65-.3 1.5 1.5 0 0 0-.9 1.4v.15a1.85 1.85 0 0 1-3.7 0v-.08a1.5 1.5 0 0 0-1-1.4 1.5 1.5 0 0 0-1.65.3l-.05.05a1.85 1.85 0 1 1-2.6-2.6l.05-.05a1.5 1.5 0 0 0 .3-1.65 1.5 1.5 0 0 0-1.4-.9H4.5a1.85 1.85 0 0 1 0-3.7h.08a1.5 1.5 0 0 0 1.4-1 1.5 1.5 0 0 0-.3-1.65l-.05-.05a1.85 1.85 0 1 1 2.6-2.6l.05.05a1.5 1.5 0 0 0 1.65.3H9.95a1.5 1.5 0 0 0 .9-1.4V4.5a1.85 1.85 0 0 1 3.7 0v.08a1.5 1.5 0 0 0 .9 1.4 1.5 1.5 0 0 0 1.65-.3l.05-.05a1.85 1.85 0 1 1 2.6 2.6l-.05.05a1.5 1.5 0 0 0-.3 1.65v.05a1.5 1.5 0 0 0 1.4.9h.15a1.85 1.85 0 0 1 0 3.7h-.08a1.5 1.5 0 0 0-1.4.9z" />
    </g>
  ),
  login: (
    <g>
      <path d="M4 4h7v16H4z" />
      <path d="M9 12h12" />
      <path d="m17 8 4 4-4 4" />
    </g>
  ),
  card: (
    <g>
      <rect x="3" y="6" width="18" height="13" rx="2.5" />
      <path d="M3 11h18" />
      <path d="M7 15.5h3" />
    </g>
  ),
  note: (
    <g>
      <path d="M14 3.5H6.5a2.5 2.5 0 0 0-2.5 2.5v12a2.5 2.5 0 0 0 2.5 2.5h11a2.5 2.5 0 0 0 2.5-2.5V9.5z" />
      <path d="M14 3.5v6h6" />
      <path d="M7.5 14h7M7.5 17.5h4.5" />
    </g>
  ),
  identity: (
    <g>
      <circle cx="12" cy="9" r="3.75" />
      <path d="M4.5 20c1-3.6 3.75-5.25 7.5-5.25S18.5 16.4 19.5 20" />
    </g>
  ),
  search: (
    <g>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.35-4.35" />
    </g>
  ),
  filter: <path d="M3 5h18l-7 9v6l-4-2v-4z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  x: <path d="M6 6l12 12M18 6 6 18" />,
  launch: (
    <g>
      <path d="M14 4h6v6" />
      <path d="M20 4 10.5 13.5" />
      <path d="M19 13.5v5.5a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 4 19V7a1.5 1.5 0 0 1 1.5-1.5H11" />
    </g>
  ),
  edit: (
    <g>
      <path d="M4 20h4l10.5-10.5a2 2 0 0 0-2.8-2.8L5.2 17.2a2 2 0 0 0-.6 1.2L4 20z" />
      <path d="m14.5 6.5 3 3" />
    </g>
  ),
  copy: (
    <g>
      <rect x="9" y="9" width="11" height="11" rx="2.5" />
      <path d="M5 15V6.5A2.5 2.5 0 0 1 7.5 4H16" />
    </g>
  ),
  eye: (
    <g>
      <path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </g>
  ),
  eyeOff: (
    <g>
      <path d="M9.5 5.4A10.5 10.5 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.4 4.3" />
      <path d="M14.5 14.6a3 3 0 0 1-4.6-3.9" />
      <path d="M6.4 6.4A17 17 0 0 0 2.5 12s3.5 6.5 9.5 6.5a10.4 10.4 0 0 0 4.6-1.1" />
      <path d="m3 3 18 18" />
    </g>
  ),
  chevronRight: <path d="m9.5 6 6 6-6 6" />,
  chevronLeft: <path d="m14.5 6-6 6 6 6" />,
  chevronDown: <path d="m6 9.5 6 6 6-6" />,
  download: (
    <g>
      <path d="M12 4v11" />
      <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />
      <path d="M5 19h14" />
    </g>
  ),
  upload: (
    <g>
      <path d="M12 4v11" />
      <path d="m6.5 9.5 5.5-5.5 5.5 5.5" />
      <path d="M5 19h14" />
    </g>
  ),
  signOut: (
    <g>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </g>
  ),
  view: (
    <g>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.35-4.35" />
    </g>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  checklist: (
    <g>
      <path d="M4 7l1.5 1.5L8.5 5.5" />
      <path d="M4 16l1.5 1.5L8.5 14.5" />
      <path d="M11.5 7.5H20" />
      <path d="M11.5 16.5H20" />
    </g>
  ),
  contact: (
    <g>
      <rect x="4" y="3.5" width="16" height="17" rx="2.5" />
      <path d="M8 3.5v17" />
      <circle cx="14" cy="10" r="2.25" />
      <path d="M11 16c.4-1.6 1.5-2.5 3-2.5s2.6.9 3 2.5" />
    </g>
  ),
  phone: (
    <path d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v3a2 2 0 0 1-2 2A15.5 15.5 0 0 1 4.5 6a2 2 0 0 1 2-2z" />
  ),
  mail: (
    <g>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="m4 7.5 8 5.5 8-5.5" />
    </g>
  ),
  folder: (
    <path d="M4 6.5a2 2 0 0 1 2-2h3l2 2.5h7a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
  ),
  calendar: (
    <g>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 9.5h17" />
      <path d="M8 3.5v3M16 3.5v3" />
    </g>
  ),
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}

export function Icon({
  name,
  size = 24,
  strokeWidth = 2,
  className,
  style,
  ...rest
}: IconProps): JSX.Element | null {
  const path = ICON_PATHS[name];
  if (!path) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flexShrink: 0, ...style }}
      {...rest}
    >
      {path}
    </svg>
  );
}
